import type { GeoDesignExportFormat } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { IconDownload } from '@shared/ui/icons';
import { ErrorText, Hint, Muted, Row, Stack } from '@widgets/GeoSchemes';
import { Sub } from './styles';

interface Props {
  elementsCount: number;
  sheetBound: boolean;
  sheetKind?: 'pdf' | 'dxf';
  dirty: boolean;
  busy: boolean;
  error: string | null;
  onExport: (format: GeoDesignExportFormat) => void;
}

export function ExportSection({
  elementsCount,
  sheetBound,
  sheetKind,
  dirty,
  busy,
  error,
  onExport,
}: Props) {
  if (elementsCount === 0 && sheetBound) {
    return (
      <Stack $gap={2}>
        <Sub>Лист РД в координатах площадки</Sub>
        <Hint>
          {sheetKind === 'pdf'
            ? 'Лист уже привязан — его графика и тексты переносятся в DXF или DWG в координатах площадки: в AutoCAD не нужно вставлять PDF, масштабировать и поворачивать.'
            : 'Лист уже привязан — DXF листа переносится в координаты площадки: в AutoCAD не нужно масштабировать и поворачивать.'}{' '}
          Каталог для прибора появится, когда будут проектные элементы.
        </Hint>
        <Row $gap={2}>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => onExport('dxf')}
            disabled={busy}
          >
            <IconDownload size={16} />
            {busy ? 'Готовим файл…' : 'Скачать лист в МСК (DXF)'}
          </Button>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={() => onExport('dwg')}
            disabled={busy}
          >
            <IconDownload size={16} />
            {busy ? 'Готовим файл…' : 'DWG 2010'}
          </Button>
        </Row>
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Stack>
    );
  }

  if (elementsCount > 0) {
    return (
      <Stack $gap={2}>
        <Sub>Вынос в натуру</Sub>
        <Hint>
          Проектные элементы в координатах площадки — без ручной оцифровки: DXF или DWG 2010 для
          AutoCAD (лист РД, перенесённый привязкой; сваи или углы контуров с метками и
          отметками; точки с Z = отметке) и каталог «имя, X, Y, H» для прибора или Excel.
        </Hint>
        <Row $gap={2}>
          {(['dxf', 'dwg', 'txt', 'csv'] as const).map((f) => (
            <Button
              key={f}
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() => onExport(f)}
              disabled={busy}
            >
              {f === 'dxf'
                ? 'DXF для AutoCAD'
                : f === 'dwg'
                  ? 'DWG 2010 для AutoCAD'
                  : f === 'txt'
                    ? 'Каталог для прибора (TXT)'
                    : 'Каталог для Excel (CSV)'}
            </Button>
          ))}
        </Row>
        {dirty && <Muted>Выгружаются сохранённые элементы — сохраните правки.</Muted>}
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Stack>
    );
  }

  return null;
}
